import { ShieldCheck } from 'lucide-react';
import {} from '../components/ui/button';
import { useLab } from '../lib/service';
import { money, BUDGET_CAP } from '@drama/domain';
import {} from '../shared';

/** Budget ceilings, trial state and the experiment ledger. */
export function Experiment() {
  const { home, isDemo } = useLab();
  const b = home?.budget;
  const metrics = home?.metrics;
  return (
    <>
      <div className="page-heading">
        <div>
          <span className="eyebrow">PHASE 0 · THE FIRST EXPERIMENT</span>
          <h1>
            Make something worth watching<span>.</span>
          </h1>
          <p>Ten ideas. Three pilots. One question: do you want to see what happens next?</p>
        </div>
      </div>
      <div className="experiment-grid">
        {[
          [money(b?.spent ?? 0), 'Used so far'],
          [money(b?.reserved ?? 0), 'Reserved for active work'],
          [money(b?.cap ?? BUDGET_CAP), 'Total experiment cap'],
        ].map(([value, title]) => (
          <div className="metric-card" key={title}>
            <span>{title}</span>
            <strong>{value}</strong>
            <small>{isDemo ? 'Simulated · no charges' : 'USD · production costs only'}</small>
          </div>
        ))}
      </div>
      <div className="experiment-grid">
        {[
          [metrics?.completedOperations ?? 0, 'Completed operations'],
          [
            metrics ? `${Math.round(metrics.averageLatencyMs / 1000)}s` : '—',
            'Average operation latency',
          ],
          [metrics?.retries ?? 0, 'Worker retries'],
          [metrics?.continuityFailures ?? 0, 'Continuity failures'],
          [metrics?.averageDialogue?.toFixed(1) ?? '—', 'Viewer dialogue score / 5'],
          [
            metrics?.viewerCount
              ? `${metrics.wantsNext} / ${metrics.viewerCount}`
              : 'Awaiting viewers',
            'Would watch Episode 2',
          ],
        ].map(([value, title]) => (
          <div className="metric-card" key={title}>
            <span>{title}</span>
            <strong>{value}</strong>
          </div>
        ))}
      </div>
      <div className="experiment-grid">
        <div className="paper-panel">
          <span className="number-label">01</span>
          <h2>Creation</h2>
          <p>Can a few sentences become a coherent 60–90 second drama?</p>
          <ul>
            <li>English dialogue and captions</li>
            <li>Persistent fictional adult casts</li>
            <li>720p vertical video, 24 fps</li>
            <li>Romance, mystery, family drama</li>
          </ul>
        </div>
        <div className="paper-panel">
          <span className="number-label">02</span>
          <h2>Continuity</h2>
          <p>Does the story hold together between shots?</p>
          <ul>
            <li>Stable faces and performances</li>
            <li>Consistent wardrobe and location</li>
            <li>Canon checked at every stage</li>
            <li>Targeted recovery from failures</li>
          </ul>
        </div>
        <div className="paper-panel">
          <span className="number-label">03</span>
          <h2>Entertainment</h2>
          <p>Would someone choose to watch the next episode?</p>
          <ul>
            <li>Feedback from approved reviewers</li>
            <li>Character and story clarity</li>
            <li>Dialogue and pacing quality</li>
            <li>Intent to watch Episode 2</li>
          </ul>
        </div>
      </div>
      <div className="notice">
        <ShieldCheck size={20} />
        <span>
          The budget is a hard application limit, not a promise of three successful pilots. If
          quality needs more work, preserve the results and learn from them.
        </span>
      </div>
    </>
  );
}
